import React from "react";
import { spring, useCurrentFrame, useVideoConfig } from "remotion";
import { LOOK } from "../../b2c/look";
import { FrakLogo } from "../../components/FrakLogo";
import type { Merchant } from "../merchants";
import { LogoTile } from "./Logo";

const pop = (frame: number, at: number, fps: number) =>
  spring({
    frame: frame - at,
    fps,
    config: { damping: 13, stiffness: 230, mass: 0.6 },
  });

/**
 * One chat bubble. `in` = a friend (white, left), `out` = you (Frak blue,
 * right), same shapes as the B2C hook so the two videos feel related.
 * Pops from its tail corner on `at`.
 */
export const Bubble: React.FC<{
  side: "in" | "out";
  at: number;
  fontSize: number;
  maxWidth: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ side, at, fontSize, maxWidth, children, style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const p = pop(frame, at, fps);
  if (frame < at) return null;
  const out = side === "out";
  const r = fontSize * 0.9;
  return (
    <div
      style={{
        alignSelf: out ? "flex-end" : "flex-start",
        maxWidth,
        padding: `${fontSize * 0.5}px ${fontSize * 0.7}px`,
        borderRadius: out
          ? `${r}px ${r}px ${r * 0.25}px ${r}px`
          : `${r}px ${r}px ${r}px ${r * 0.25}px`,
        background: out ? LOOK.blue : LOOK.white,
        color: out ? LOOK.white : LOOK.ink,
        fontFamily: LOOK.body,
        fontWeight: LOOK.semi,
        fontSize,
        lineHeight: 1.25,
        letterSpacing: -fontSize * 0.01,
        textWrap: "balance",
        boxShadow: "0 18px 40px -14px rgba(0,0,0,0.45)",
        scale: p,
        transformOrigin: out ? "100% 100%" : "0% 100%",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** The shared link, as a messaging app previews it: logo, name, site. */
export const LinkPreview: React.FC<{ m: Merchant; fontSize: number }> = ({
  m,
  fontSize,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: fontSize * 0.5,
      marginTop: fontSize * 0.45,
      padding: fontSize * 0.28,
      paddingRight: fontSize * 0.7,
      borderRadius: fontSize * 0.55,
      background: "rgba(255,255,255,0.16)",
    }}
  >
    <LogoTile m={m} size={fontSize * 2.1} style={{ boxShadow: "none" }} />
    <div style={{ lineHeight: 1.15, minWidth: 0 }}>
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: fontSize * 1.02,
        }}
      >
        {m.name}
      </div>
      {m.site ? (
        <div
          style={{
            fontWeight: LOOK.medium,
            fontSize: fontSize * 0.72,
            opacity: 0.85,
          }}
        >
          {m.site}
        </div>
      ) : null}
    </div>
  </div>
);

/**
 * Frak push notification (iOS style), the B2C "you just earned" card with
 * no amount: a sale through your link, attributed to the brand.
 */
export const Notification: React.FC<{
  at: number;
  width: number;
  app: string;
  time: string;
  title: string;
  body: string;
}> = ({ at, width, app, time, title, body }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const p = spring({
    frame: frame - at,
    fps,
    config: { damping: 16, stiffness: 170, mass: 0.7 },
  });
  const k = width / 900;
  return (
    <div
      style={{
        width,
        display: "flex",
        gap: 26 * k,
        alignItems: "center",
        padding: `${30 * k}px ${34 * k}px`,
        borderRadius: 46 * k,
        background: "rgba(255,255,255,0.94)",
        boxShadow: "0 30px 70px -20px rgba(0,0,0,0.5)",
        translate: `0 ${(1 - p) * -160}%`,
        opacity: Math.min(1, p * 2),
      }}
    >
      <div
        style={{
          flex: "none",
          width: 96 * k,
          height: 96 * k,
          borderRadius: 24 * k,
          background: LOOK.blue,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <FrakLogo variant="icon" width={56 * k} color={LOOK.white} />
      </div>
      <div
        style={{ flex: 1, minWidth: 0, fontFamily: LOOK.body, color: LOOK.ink }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 26 * k,
            fontWeight: LOOK.semi,
            color: LOOK.inkSoft,
            letterSpacing: 1,
          }}
        >
          <span>{app}</span>
          <span style={{ letterSpacing: 0, fontWeight: LOOK.medium }}>
            {time}
          </span>
        </div>
        <div
          style={{
            fontSize: 40 * k,
            fontWeight: LOOK.bold,
            marginTop: 4 * k,
            letterSpacing: -0.5,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 32 * k,
            fontWeight: LOOK.medium,
            color: LOOK.inkSoft,
            marginTop: 4 * k,
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
};
